import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, ClipboardList } from "lucide-react";
import { PointsBadge } from "./PointsBadge";
import { EmptyState } from "./EmptyState";
import { useStore } from "../store/useStore";
import { z } from "zod";

interface Task {
  uuid: string;
  name: string;
  points: number;
  frequency_number: number;
  frequency_unit: string;
  is_high_priority?: boolean | null;
  sort_order?: number | null;
}

interface SortableTaskItemProps {
  task: Task;
  onEdit: (taskId: string) => void;
}

const SortableTaskItem = ({ task, onEdit }: SortableTaskItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-card rounded-3xl p-3 pl-2 border border-border transition-shadow ${
        isDragging ? "shadow-raised ring-2 ring-primary" : "shadow-soft"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Drag Handle */}
          <div
            className="inline-flex items-center justify-center w-8 min-h-[44px] rounded-xl text-muted-foreground/70 hover:text-foreground hover:bg-surface transition-colors cursor-grab active:cursor-grabbing touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            {...attributes}
            {...listeners}
            aria-label={`Reorder ${task.name}`}
          >
            <GripVertical className="w-4 h-4" aria-hidden="true" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-foreground font-bold truncate">{task.name}</h3>
            <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <span>
                Every {task.frequency_number} {task.frequency_unit}
              </span>
              {task.is_high_priority && (
                <span className="bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200 px-2 py-0.5 rounded-md text-xs font-semibold">
                  High Priority
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <PointsBadge points={task.points} />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(task.uuid);
            }}
            aria-label={`Edit ${task.name}`}
            className="inline-flex items-center justify-center w-11 h-11 rounded-full text-muted-foreground hover:text-primary hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

interface SortableTaskListProps {
  tasks: Task[];
  onEdit: (taskId: string) => void;
  onReorder?: (tasks: Task[]) => void;
}

const SortableTaskList = ({ tasks, onEdit }: SortableTaskListProps) => {
  const updateTaskOrder = useStore((state) => state.updateTaskOrder);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sort tasks by sort_order, falling back to creation order
  const sortedTasks = [...tasks].sort((a, b) => {
    const aOrder = a.sort_order || 0;
    const bOrder = b.sort_order || 0;
    return aOrder - bOrder;
  });

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sortedTasks.findIndex((task) => task.uuid === active.id);
    const newIndex = sortedTasks.findIndex((task) => task.uuid === over.id);

    if (oldIndex === newIndex) return;

    // Calculate the new sort order based on position
    let newSortOrder: number;

    const SORT_ORDER_INCREMENT = 10;
    if (newIndex === 0) {
      // Moving to the beginning
      const firstSortOrder = sortedTasks[0].sort_order || SORT_ORDER_INCREMENT;
      newSortOrder = firstSortOrder / 2;
    } else if (newIndex === sortedTasks.length - 1) {
      // Moving to the end
      const lastSortOrder =
        sortedTasks[sortedTasks.length - 1].sort_order || SORT_ORDER_INCREMENT;
      newSortOrder = lastSortOrder + 10;
    } else {
      // Moving between two items
      let prevSortOrder: number;
      let nextSortOrder: number;

      if (newIndex < oldIndex) {
        // Moving up
        prevSortOrder = sortedTasks[newIndex - 1]?.sort_order ?? 0;
        nextSortOrder =
          sortedTasks[newIndex].sort_order ?? SORT_ORDER_INCREMENT;
      } else {
        // Moving down
        prevSortOrder = sortedTasks[newIndex].sort_order ?? 0;
        nextSortOrder =
          sortedTasks[newIndex + 1]?.sort_order ?? SORT_ORDER_INCREMENT;
      }

      // Calculate the middle point
      newSortOrder = (prevSortOrder + nextSortOrder) / 2;
    }

    // Update the task order
    try {
      // Parse active.id to ensure it's a string (task UUID)
      try {
        const taskId = z.string().parse(active.id);
        await updateTaskOrder(taskId, newSortOrder);
      } catch (error) {
        console.error("Invalid task ID:", error);
      }
    } catch (error) {
      console.error("Failed to reorder task:", error);
      // Could add toast notification here
    }
  };

  if (sortedTasks.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No tasks yet"
        description="Create tasks to organize this area"
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedTasks.map((t) => t.uuid)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {sortedTasks.map((task) => (
            <SortableTaskItem key={task.uuid} task={task} onEdit={onEdit} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableTaskList;
